/* eslint-disable no-unused-vars */
/* eslint-disable consistent-return */
import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
// routes
import { toast } from 'react-toastify';
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
import { LOGIN_AS_USER } from '../../../graphql/Auth';
import { onAuthenticated } from '../../../redux/slices/auth';
import { ACTIVE_SUBSCRIPTION_STORE_ID, ME } from '../../../graphql/User';
import { client } from '../../../graphql/Server';
import { setSession } from '../../../utils/jwt';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const dispatch = useDispatch();
  const { Login } = useAuth();
  const navigate = useNavigate();
  const [loginAsUser] = useMutation(LOGIN_AS_USER);

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: 'superadmin@hnh.pk',
    password: '12345678',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;
  const onSubmit = async (data) => {
    try {
      const response = await loginAsUser({
        variables: {
          email: data.email,
          password: data.password,
        },
      });

      const token = response?.data?.loginAsUser?.token;

      if (response.data.loginAsUser.otpRequired) {
        return navigate(`/auth/verify?email=${data.email}`);
      }

      localStorage.setItem('seller', JSON.stringify(response.data.loginAsUser.user));

      // 🔍 Debug authorization header
      console.log('Login Token:', token);
      setSession(token)
      const meResponse = await client.query({
        query: ME,
        fetchPolicy: 'no-cache',
      });
      console.log('meResponse:', meResponse);
      const store = meResponse?.data?.me?.store;

      const storeId = meResponse?.data?.me?.store?.id;
      console.log('Store ID:', storeId);

      if (!store) {
        toast.error("Please register your vander account.");
        return navigate("/auth/register");
      }

      const subscriptionRes = await client.query({
        query: ACTIVE_SUBSCRIPTION_STORE_ID,
        variables: { storeId },
        fetchPolicy: 'no-cache',
      });
      const subscription = subscriptionRes.data?.activeSubscriptionByStoreId;
      
      Login(response.data.loginAsUser);
      dispatch(onAuthenticated(response.data.loginAsUser));

    } catch (error) {
      reset();
      console.error('Login error:', error);
      if (isMountedRef.current) {
        setError('afterSubmit', {
          type: 'manual',
          message: error.message || 'Something went wrong',
        });
      }
    }
  };


  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Remember me" />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      <Stack direction="column" sx={{ my: 4 }}>
        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
          Login
        </LoadingButton>
        <Button fullWidth size="large" type="button" variant="text" onClick={() => navigate('/auth/register')}>
          Create Store
        </Button>
      </Stack>
    </FormProvider>
  );
}
